import type { Task } from '@/contexts/TasksContext'
import type { Project } from '@/store/projects/projectsSlice'

// Client-side global search over the already-loaded store (SCD-171). Pure functions so the
// modal stays presentational and this stays testable. Search is by number or name only.

export interface TaskSearchResult {
    kind: 'task'
    id: string
    number: number
    name: string
    projectId: string
}

export interface ProjectSearchResult {
    kind: 'project'
    id: string
    name: string
}

export type SearchResult = TaskSearchResult | ProjectSearchResult

const RESULT_LIMIT = 20

const toSeconds = (ts: { seconds?: number } | null | undefined): number => ts?.seconds ?? 0

const taskResult = (t: Task): TaskSearchResult => ({
    kind: 'task',
    id: t.id,
    number: t.number,
    name: t.name,
    projectId: t.projectId,
})

// The most recently active tasks, shown when the query is empty.
export function getRecentTasks(tasks: Task[], limit: number): TaskSearchResult[] {
    return [...tasks]
        .filter(t => !t.pending)
        .sort((a, b) => toSeconds(b.updatedAt) - toSeconds(a.updatedAt))
        .slice(0, limit)
        .map(taskResult)
}

export function searchTasksAndProjects(query: string, tasks: Task[], projects: Project[]): SearchResult[] {
    const q = query.trim().toLowerCase()
    if (!q) return []

    // A number query — "123" or "scd-123" — matches by task number (the prefix is ignored).
    const digits = q.match(/^(?:[a-z]+-)?(\d+)$/)?.[1]

    type Scored = { result: SearchResult; score: number; updatedAt: number }
    const scored: Scored[] = []

    for (const t of tasks) {
        if (t.pending) continue
        const name = (t.name || '').toLowerCase()
        const num = String(t.number)
        let score = 0
        if (digits) {
            if (num === digits) score = 100
            else if (num.startsWith(digits)) score = 85
            else if (num.includes(digits)) score = 70
        }
        if (!score && name) {
            if (name.startsWith(q)) score = 80
            else if (name.includes(q)) score = 60
        }
        if (score) scored.push({ result: taskResult(t), score, updatedAt: toSeconds(t.updatedAt) })
    }

    for (const p of projects) {
        const name = (p.name || '').toLowerCase()
        let score = 0
        if (name.startsWith(q)) score = 75
        else if (name.includes(q)) score = 55
        if (score) scored.push({ result: { kind: 'project', id: p.id, name: p.name }, score, updatedAt: toSeconds(p.updatedAt) })
    }

    scored.sort((a, b) => b.score - a.score || b.updatedAt - a.updatedAt)
    return scored.slice(0, RESULT_LIMIT).map(s => s.result)
}
